import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { clearLegacyTokens } from "../utils/device";

/*
 * Session-based authentication.
 *
 * The server keeps the session and sets an httpOnly cookie; this app never
 * receives or stores a token. Only the signed-in user is held in memory.
 */

const AuthContext = createContext(null);

const CUSTOMER_URL = "https://grapheweddings.com";
const ADMIN_LOGIN = "/login";
const ALLOWED_ROLE = "admin";

/*
 * Thrown by login() when the admin is already signed in on the maximum
 * number of devices. `data` holds the limit, the one-time challenge token
 * and the active sessions to choose from.
 */
export class DeviceLimitError extends Error {
  constructor(message, data) {
    super(message);
    this.name = "DeviceLimitError";
    this.code = "DEVICE_LIMIT_REACHED";
    this.data = data;
  }
}

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const clearAuth = useCallback(() => {
    clearLegacyTokens();
    setUser(null);
  }, []);

  const acceptUser = useCallback(
    (userData) => {
      if (!userData || userData.role !== ALLOWED_ROLE) {
        clearAuth();
        return false;
      }

      setUser(userData);
      return true;
    },
    [clearAuth]
  );

  // Reflect self-service profile edits (name, email, photo) without a reload.
  const updateUser = useCallback((changes) => {
    setUser((current) => (current ? { ...current, ...changes } : current));
  }, []);

  const checkAuthStatus = useCallback(async () => {
    try {
      const res = await api.get("/auth/verify", { skipAuthRedirect: true });
      return acceptUser(res.data?.data?.user);
    } catch {
      clearAuth();
      return false;
    }
  }, [acceptUser, clearAuth]);

  const initialiseAuth = useCallback(async () => {
    setAuthLoading(true);

    try {
      await checkAuthStatus();
    } finally {
      setAuthLoading(false);
    }
  }, [checkAuthStatus]);

  const runAuthRequest = useCallback(
    async (request) => {
      let res;

      try {
        res = await request();
      } catch (error) {
        const data = error.response?.data;

        if (data?.code === "DEVICE_LIMIT_REACHED") {
          throw new DeviceLimitError(data.message, data.data);
        }

        // A customer tried to sign in here: send them to the storefront.
        if (data?.code === "WRONG_APP" && data.data?.role === "customer") {
          window.location.replace(`${CUSTOMER_URL}/login`);
        }

        throw error;
      }

      if (!acceptUser(res.data?.data?.user)) {
        throw new Error("This panel is only for admins.");
      }

      clearLegacyTokens();
      return res.data;
    },
    [acceptUser]
  );

  const login = ({ email, password }) =>
    runAuthRequest(() => api.post("/auth/login", { email, password }));

  // Finish a login that hit the device limit by signing out chosen devices.
  const resolveLogin = ({ challengeToken, revokeSessionUids }) =>
    runAuthRequest(() =>
      api.post("/auth/login/resolve", {
        challenge_token: challengeToken,
        revoke_session_uids: revokeSessionUids,
      })
    );

  const logout = async () => {
    try {
      await api.post("/auth/logout", null, { skipAuthRedirect: true });
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      clearAuth();

      navigate(ADMIN_LOGIN, {
        replace: true,
      });
    }
  };

  useEffect(() => {
    initialiseAuth();
  }, [initialiseAuth]);

  // The API client reports a revoked/expired session; drop the user.
  useEffect(() => {
    const handleSessionEnded = () => setUser(null);

    window.addEventListener("auth-session-ended", handleSessionEnded);
    return () =>
      window.removeEventListener("auth-session-ended", handleSessionEnded);
  }, []);

  // Re-check when the tab regains focus so a revoked session ends promptly.
  useEffect(() => {
    let timer;

    const run = () => {
      clearTimeout(timer);
      timer = setTimeout(checkAuthStatus, 300);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") run();
    };

    window.addEventListener("focus", run);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("focus", run);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearTimeout(timer);
    };
  }, [checkAuthStatus]);

  return (
    <AuthContext.Provider
      value={{
        user,
        role: user?.role || null,
        isAuthenticated: !!user,
        authLoading,
        login,
        resolveLogin,
        logout,
        clearAuth,
        checkAuthStatus,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
